import * as React from 'react';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { BasicToutViewController } from '~controllers/BasicToutViewController';
import {
    INodeData,
    INodeContent,
    IMantleMedia,
    IMantleRichText,
    IMantleCtaButton
} from '~domain/entities/Node';
import { Spinner } from '@estee/elc-spinner';
import { lazyInject } from '~setup/diContainer';
import serviceNames from '~setup/ContentService';
import { ClickableBlock } from '~views/ClickableBlock';
import { MantleMedia } from '~views/fields/MantleMedia';
import { ContentOver } from '~views/ContentOver';
import { AdditionalContent, ContentPosition } from '~views/AdditionalContent';
import { BASIC_TOUT } from '~constants/BasicTout';

interface IBasicToutDisplayMedia {
    image_mantle: IMantleMedia;
}

interface IBasicToutDisplayContentAboveImage {
    eyebrow_above_image: IMantleRichText;
    header_above_image: IMantleRichText;
    content_above_image: IMantleRichText;
    primary_cta_button_above_image: IMantleCtaButton;
    secondary_cta_button_above_image: IMantleCtaButton;
    content_above_image_hide_mobile: string;
    content_above_image_hide_pc: string;
    text_alignment_above_image: string;
}

interface IBasicToutDisplayContentOverImage {
    eyebrow_over_media: IMantleRichText;
    header_over_media: IMantleRichText;
    content_over_media: IMantleRichText;
    cta_button_over_media: IMantleCtaButton;
    cta_button_2_over_media: IMantleCtaButton;
    text_row_1_over_media: IMantleRichText;
    text_row_2_over_media: IMantleRichText;
    horizontal_alignment_over_media: string;
    vertical_alignment_over_media: string;
    max_width_over_media: string;
    margin_right_over_media: string;
    margin_left_over_media: string;
    margin_top_over_media: string;
    margin_bottom_over_media: string;
    content_over_image_hide_mobile_over_media: string;
    content_over_image_hide_pc_over_media: string;
    text_alignment_over_media: string;
    background_color: { hex: string };
}

interface IBasicToutDisplayContentBelowImage {
    eyebrow_below_image: IMantleRichText;
    header_below_image: IMantleRichText;
    content_below_image: IMantleRichText;
    cta_button_below_image: IMantleCtaButton;
    cta_button_2_below_image: IMantleCtaButton;
    content_below_image_hide_mobile: string;
    content_below_image_hide_pc: string;
    text_alignment_below_image: string;
}

interface IBasicToutNodeData extends INodeData {
    displayMedia: IBasicToutDisplayMedia;
    displayContentAboveImage: IBasicToutDisplayContentAboveImage;
    displayContentOverImage: IBasicToutDisplayContentOverImage;
    displayContentBelowImage: IBasicToutDisplayContentBelowImage;
    moduleUrl: string | null;
}

export interface IBasicToutNodeContent extends INodeContent {
    data: IBasicToutNodeData[];
}

export interface IBasicToutProps {
    name: string;
    config: IBasicToutNodeContent;
    viewController: BasicToutViewController;
}

const BasicToutWrapper = styled.div`
    position: relative;
`;

const BasicToutMediaWrapper = styled.div`
    background-color: ${(props) =>
        props.theme.backgroundColor ? props.theme.backgroundColor.hex : null};
`;

export interface IBasicToutModuleLink {
    className?: string;
    children: JSX.Element;
    isToutClickable: boolean;
    onClick?(): void;
}

const BasicToutModuleLinkWrapper = observer((props: IBasicToutModuleLink) => {
    if (props.isToutClickable) {
        return (
            <ClickableBlock className={props.className} onClick={props.onClick}>
                {props.children}
            </ClickableBlock>
        );
    }

    return <div className={props.className}>{props.children}</div>;
});

/* eslint-disable complexity */
@observer
class BasicTout extends React.Component<IBasicToutProps> {
    static displayName = 'BasicTout';
    public async componentDidMount() {
        await this.props.viewController.loaded(this.props);
    }

    @lazyInject(serviceNames.basicToutViewController)
    private basicToutViewController: BasicToutViewController;

    public getField = (field: string) => {
        return { __html: field };
    };

    public render() {
        const { nid } = this.props.config;
        const content = this.props.viewController.getContent(nid);
        if (!content) {
            return <Spinner isLoading={true} />;
        }
        const { layout, data } = content;
        const {
            displayMedia: { image_mantle: imageMantle },
            displayContentAboveImage,
            displayContentOverImage: {
                eyebrow_over_media: eyebrowOverMedia,
                header_over_media: headerOverMedia,
                content_over_media: contentOverMedia,
                cta_button_over_media: ctaButtonOverMedia,
                cta_button_2_over_media: ctaButton2OverMedia,
                text_row_1_over_media: textRow1OverMedia,
                text_row_2_over_media: textRow2OverMedia,
                horizontal_alignment_over_media: horizontalAlignmentOverMedia,
                vertical_alignment_over_media: verticalAlignmentOverMedia,
                max_width_over_media: maxWidthOverMedia,
                margin_right_over_media: marginRightOverMedia,
                margin_left_over_media: marginLeftOverMedia,
                margin_top_over_media: marginTopOverMedia,
                margin_bottom_over_media: marginBottomOverMedia,
                content_over_image_hide_mobile_over_media: contentOverImageHideMobileOverMedia,
                content_over_image_hide_pc_over_media: contentOverImageHidePcOverMedia,
                text_alignment_over_media: textAlignmentOverMedia,
                background_color: backgroundColorOverMedia
            },
            displayContentBelowImage: {
                eyebrow_below_image: eyebrowBelowMedia,
                header_below_image: headerBelowMedia,
                content_below_image: contentBelowMedia,
                cta_button_below_image: ctaButtonBelowMedia,
                cta_button_2_below_image: ctaButton2BelowMedia,
                content_below_image_hide_mobile: contentBelowImageHideMobile,
                content_below_image_hide_pc: contentBelowImageHidePc,
                text_alignment_below_image: textAlignmentBelowImage
            },
            moduleUrl
        } = data[0];

        const {
            eyebrow_above_image: eyebrowAboveMedia,
            header_above_image: headerAboveMedia,
            content_above_image: contentAboveMedia,
            primary_cta_button_above_image: primaryCtaButtonAboveMedia,
            secondary_cta_button_above_image: secondaryCtaButtonAboveMedia,
            content_above_image_hide_mobile: contentAboveImageHideMobile,
            content_above_image_hide_pc: contentAboveImageHidePc,
            text_alignment_above_image: textAlignmentAboveImage
        } = displayContentAboveImage || {};

        const { redirectToSiteUrl } = this.basicToutViewController;
        const handleClick = () => redirectToSiteUrl(moduleUrl);

        const basicToutModuleLinkWrapperClassNames = classnames(
            'elc-basic-tout__link-content',
            'js-basic-tout__link-content',
            'basic-tout',
            'elc-basic-tout',
            'content-block',
            'basic-tout__link-content'
        );

        const basicToutWrapperClassNames = classnames(
            'elc-service-content',
            'js-service-content',
            'basic-tout__wrapper',
            'content-block__content-wrapper',
            `elc-service-content__layout-${layout}`
        );

        const basicToutMediaWrapperClassNames = classnames(
            'elc-basic-tout__media-wrapper',
            'js-basic-tout__media-wrapper',
            'basic-tout__media-wrapper',
            'content-block',
            'basic-tout__media-wrapper'
        );

        const basicToutMediaClassNames = classnames(
            'elc-basic-tout__media',
            'js-basic-tout__media',
            'basic-tout__media',
            'align-center',
            'basic-tout__media'
        );

        const isToutClickable = moduleUrl !== '' && moduleUrl !== null;

        return (
            <BasicToutModuleLinkWrapper
                className={basicToutModuleLinkWrapperClassNames}
                isToutClickable={isToutClickable}
                onClick={handleClick}
            >
                <BasicToutWrapper className={basicToutWrapperClassNames}>
                    {displayContentAboveImage && (
                        <AdditionalContent
                            position={ContentPosition.ABOVE}
                            layoutName={BASIC_TOUT}
                            eyebrow={eyebrowAboveMedia}
                            header={headerAboveMedia}
                            content={contentAboveMedia}
                            primaryCtaButton={primaryCtaButtonAboveMedia}
                            secondaryCtaButton={secondaryCtaButtonAboveMedia}
                            hideMobile={contentAboveImageHideMobile}
                            hidePc={contentAboveImageHidePc}
                            textAlignment={textAlignmentAboveImage}
                        />
                    )}
                    <BasicToutMediaWrapper
                        className={basicToutMediaWrapperClassNames}
                        theme={{ backgroundColor: backgroundColorOverMedia }}
                    >
                        <MantleMedia {...imageMantle} className={basicToutMediaClassNames} />
                        <ContentOver
                            layoutName={BASIC_TOUT}
                            eyebrow={eyebrowOverMedia}
                            header={headerOverMedia}
                            content={contentOverMedia}
                            primaryCtaButton={ctaButtonOverMedia}
                            secondaryCtaButton={ctaButton2OverMedia}
                            textRow1={textRow1OverMedia}
                            textRow2={textRow2OverMedia}
                            horizontalAlignment={horizontalAlignmentOverMedia}
                            verticalAlignment={verticalAlignmentOverMedia}
                            maxWidth={maxWidthOverMedia}
                            marginRight={marginRightOverMedia}
                            marginLeft={marginLeftOverMedia}
                            marginTop={marginTopOverMedia}
                            marginBottom={marginBottomOverMedia}
                            imageHideMobile={contentOverImageHideMobileOverMedia}
                            imageHidePc={contentOverImageHidePcOverMedia}
                            textAlignment={textAlignmentOverMedia}
                            hasImage={!!imageMantle.html}
                        />
                    </BasicToutMediaWrapper>
                    <AdditionalContent
                        position={ContentPosition.BELOW}
                        layoutName={BASIC_TOUT}
                        eyebrow={eyebrowBelowMedia}
                        header={headerBelowMedia}
                        content={contentBelowMedia}
                        primaryCtaButton={ctaButtonBelowMedia}
                        secondaryCtaButton={ctaButton2BelowMedia}
                        hideMobile={contentBelowImageHideMobile}
                        hidePc={contentBelowImageHidePc}
                        textAlignment={textAlignmentBelowImage}
                    />
                </BasicToutWrapper>
            </BasicToutModuleLinkWrapper>
        );
    }
}

export default BasicTout;
